/**
 * Search gates — the bounded thresholds that decide what a search DOES.
 *
 * These live in shared/ (not server/) because the search bar is a client
 * component and cannot import from server modules; kit-constants.ts lives here
 * for the same reason.
 *
 * WHY THEY ARE FRACTIONS NOW
 * The gates used to be absolute points on the additive relevance score:
 * GOOD_MATCH_THRESHOLD = 5000 and PERFECT_MATCH_THRESHOLD = 7000. That score is
 * unbounded and its scale depends on the query, so one threshold could not
 * serve two searches — measured, a 1-word query reached 25000-29000 while a
 * 4-word query topped out near 7000 against the same gates. The failure that
 * exposed it: "veste de sauvetage pour kayak" (a life jacket) AUTO-REDIRECTED
 * to "Veste chauffante pour femme" (a heated women's jacket) at 7200, earned by
 * matching `veste` and the preposition `pour`.
 *
 * `coverage` and `specificity` come from search_url_mappings_v2 (migration
 * 0025) and are both in [0,1] by construction, so a threshold on them means the
 * same thing for every query in every locale.
 *
 * HOW THEY WERE CALIBRATED
 * The pre-existing 437-case sample could not set them: every one of its cases
 * assumes the right page EXISTS, so it had zero examples of the failure class
 * these gates exist to stop. An `unanswerable` tier was built for this
 * (scripts/eval/build-unanswerable-sample.ts, 80 cases labeled by a criterion
 * independent of anything here — a query's two most discriminative lexemes
 * never co-occurring on any page in the region), then DISPLAY_COVERAGE was
 * swept 0.55→0.80 on the dev split and confirmed once on holdout.
 *
 * The measured trade (dev, 64 unanswerable + 374 answerable cases; baseline is
 * match capture 92.2%, declined unanswerable 53.1%):
 *     0.65  match capture 92.5%   declined unanswerable 73.4%
 *     0.70  match capture 92.0%   declined unanswerable 85.9%   ← chosen
 *     0.75  match capture 89.6%   declined unanswerable 95.3%
 *     0.80  match capture 88.8%   declined unanswerable 98.4%
 * 0.75 and above leave the harness's 2.0% match-capture tolerance. Between 0.65
 * and 0.70, 0.70 buys 12.5 points of declining for 0.8 points of capture, and
 * at 0.70 the count of INVENTED AUTO-REDIRECTS on that tier is zero — the
 * outcome this whole change exists to prevent. Holdout agreed (declined
 * 50.0% → 87.5%, match capture +7.5%, correct-is-top +10.0%), and the
 * out-of-distribution traffic sweep improved with it (200 real pages: self-hit
 * 143 → 161, results-went-empty 21 → 9).
 *
 * Re-run the sweep if the catalogue's shape changes materially; the harness
 * commands are in scripts/eval/search-suggestion-eval.ts (mind the arm-specific
 * candidate cache documented there).
 */

import type { AlgoliaHit } from '../types/search.type';
import { isNearExactSubjectMatch } from './fuzzy-subject-match';

/**
 * "Do we have an answer at all?" Below this, the search offers to create a new
 * page instead of showing what it found. Replaces GOOD_MATCH_THRESHOLD = 5000.
 *
 * Measured on the reproductions: "veste de sauvetage pour kayak" tops out at
 * coverage 0.371 and "life jacket for kayaking" at 0.430 — both are queries
 * whose product genuinely is not in the catalogue, and both must land below
 * this line. "wireless headphones" and "cordless hair trimmer" reach 1.000.
 * Swept and confirmed on holdout — see the header.
 */
export const DISPLAY_COVERAGE = 0.7;

/**
 * Shelf membership, as a fraction of the best hit's coverage. A flat gate would
 * show a single card for a broad query like "kayak" (where every hit covers the
 * one word fully); this keeps the shelf populated while still excluding the
 * long tail. Applied together with DISPLAY_COVERAGE, whichever binds harder.
 */
export const SHELF_RELATIVE_RATIO = 0.6;

/** Absolute floor for shelf membership, so a weak best hit cannot drag the
 *  relative floor down to nothing. */
export const SHELF_ABSOLUTE_FLOOR = 0.3;

/**
 * Auto-redirect: skip the shelf and open the page. Replaces
 * PERFECT_MATCH_THRESHOLD = 7000. Deliberately near-total coverage — sending a
 * shopper straight to the wrong page is the most expensive mistake this system
 * can make, and it is the one that was happening.
 */
export const REDIRECT_COVERAGE = 0.95;

/** Auto-redirect also requires the page to be substantially ABOUT the query
 *  rather than merely containing it — a hub page that mentions everything
 *  covers everything. */
export const REDIRECT_SPECIFICITY = 0.35;

/**
 * Auto-redirect requires the page's OWN NAME to account for the query, not just
 * its synonym list. This is the gate that stops a generic parent from swallowing
 * a specific query: measured, "glass coffee table" had the generic "Coffee
 * Tables" page at coverage 1.000 AND specificity 1.000 — specificity cannot
 * catch it, because a page whose subject is a strict SUBSET of the query has
 * nothing unrequested to penalise — while its subjectCoverage is 0.692 against
 * the specific page's 1.000.
 *
 * Deliberately near-total: sending a shopper to the wrong page is the most
 * expensive mistake this system makes, and a page that cannot name what they
 * asked for has not earned it. Pages below this still rank and are still shown;
 * they just do not skip the shelf.
 */
export const REDIRECT_SUBJECT_COVERAGE = 0.9;

/**
 * Auto-redirect requires this much daylight over the runner-up, on coverage OR
 * specificity. This is what stops an ambiguous query from being answered as if
 * it were precise: measured, 16 pages all return coverage 1.000 for "kayak"
 * (they genuinely all answer it) with the top two separated by 0.002 on
 * specificity — so no redirect fires and the shopper gets the shelf, which is
 * the correct product behaviour for an ambiguous one-word query.
 */
export const REDIRECT_MARGIN = 0.15;

/**
 * A one-segment URL with no page (`/en-CA/bath-mat`) is sent to a page by the
 * server only when the auto-open rule passes AND the slug's words account for
 * nearly all of that page's own name: specificity at this floor, mirroring
 * REDIRECT_SUBJECT_COVERAGE, so the words and the name account for each other.
 * A server redirect has no shelf and no conversation to correct it, and a slug
 * is a guessed address, not a query somebody typed and can refine.
 *
 * Measured on production's stored redirects (2026-09-28, 155 live rows, run
 * through frontend/server/not-found/resolve-dead-slug.ts by
 * scripts/qa-2026-09-28/deadlinks-eval-dead-slug-resolver.ts): every hit that
 * cleared the auto-open rule but named another thing scored 0.37-0.57
 * ("bath-accessories" → "Horse Bathing and Drying Accessories" 0.55,
 * "shop-by-condition" → "Air Conditioning Repair Shops" 0.57, "toilet-accessories"
 * → "Electronic dollhouse … toilet bathroom accessories" 0.37), and every right
 * one 1.0 ("bath-mat" → "Bath Mats", "canes-and-tips" → "Cane Tips").
 */
export const DEAD_SLUG_REDIRECT_SPECIFICITY = 0.9;

/**
 * Kit element reuse. Sits between DISPLAY_COVERAGE and REDIRECT_COVERAGE, the
 * same position KIT_ELEMENT_REUSE_THRESHOLD = 6500 held between the old 5000
 * and 7000 — see kit-constants.ts for why element topics need more than
 * display-grade confidence but less than redirect-grade.
 */
export const KIT_ELEMENT_REUSE_COVERAGE = 0.7;

/** Legacy absolute thresholds, still applied when a hit carries no `coverage`
 *  (SEARCH_COVERAGE=off, or a search-cache blob written before the rollout). */
export const LEGACY_GOOD_MATCH_THRESHOLD = 5000;
export const LEGACY_PERFECT_MATCH_THRESHOLD = 7000;

/**
 * A hit with a NET-NEGATIVE additive score has more penalty evidence than
 * match evidence — the scorer found contradictions or unmatched-modifier
 * pileups that outweigh everything it liked. Coverage cannot see that (it only
 * counts query-side matches), and the two measures genuinely disagreed in the
 * wild: the French query "veste de sauvetage" typed on en-CA put ten unrelated
 * French pages on the shelf at coverage 0.724 with scores down to -9700,
 * because foreign tokens carry maximal IDF in the wrong slice (see
 * search-query-language.ts). Coverage still gates and the score still orders —
 * but the score's SIGN, which is scale-free, is allowed to veto display.
 * Strictly NEGATIVE only: the trigram-fuzzy arm legitimately produces score-0
 * rows for typo'd queries ("hedphones" recovers its headphone pages at score 0,
 * coverage 1.0), where zero means "no additive signal", not "contradicted".
 * Absent score (pre-rollout cache blob) passes, keeping the fallback atomic.
 */
const hasPositiveEvidence = (hit: { relevanceScore?: number }): boolean =>
  hit.relevanceScore === undefined || hit.relevanceScore >= 0;

/** A hit qualifies for the shelf / "we have an answer" decision. Falls back to
 *  the legacy absolute score whenever coverage is absent, which is what makes
 *  the SEARCH_COVERAGE kill-switch atomic without the client reading any env. */
export const passesDisplayGate = (
  hit: { coverage?: number; relevanceScore?: number } | null | undefined,
): boolean =>
  !hit
    ? false
    : typeof hit.coverage === 'number'
      ? hit.coverage >= DISPLAY_COVERAGE && hasPositiveEvidence(hit)
      : (hit.relevanceScore ?? 0) >= LEGACY_GOOD_MATCH_THRESHOLD;

/**
 * Shelf MEMBERSHIP for an individual hit, given the floor derived from the
 * best-covered candidate (`max(SHELF_ABSOLUTE_FLOOR, best * SHELF_RELATIVE_RATIO)`).
 * Split from `passesDisplayGate` because membership is relative while
 * answerability is absolute — but both veto on net-negative evidence, and
 * keeping the veto here means every consumer (search bar, eval pipeline)
 * agrees by construction.
 */
export const passesShelfFloor = (
  hit: { coverage?: number; relevanceScore?: number } | null | undefined,
  shelfFloor: number,
): boolean =>
  !hit
    ? false
    : typeof hit.coverage === 'number'
      ? hit.coverage >= shelfFloor && hasPositiveEvidence(hit)
      : passesDisplayGate(hit);

/**
 * Near-miss shelf rescue when nothing clears DISPLAY_COVERAGE.
 * Hiking flagship sat at coverage 0.679 (missing query mass `day`) with a
 * 8000+ score — close enough to show, not close enough to auto-open.
 * Must not change DISPLAY_COVERAGE or canAutoRedirect.
 */
export const NEAR_MISS_COVERAGE = 0.65;
export const NEAR_MISS_SCORE = 8000;

/**
 * Hiking flagship is a measurement miss (`day` dropped, `20-30l` kept).
 * Unanswerable class queries with no unit ("external graphics cards") must
 * not ride this rescue. Years (`2024`) are not units.
 */
const HAS_MEASUREMENT = /\d+(?:\s*[-–]\s*\d+)?\s*(?:l|ml|cl|kg|g|gb|tb|mb|cm|mm|in|inch|inches|w|oz|lb|lbs)\b/i;

export const passesNearMissRescue = (
  hit: { coverage?: number; relevanceScore?: number } | null | undefined,
  query?: string,
): boolean =>
  !!hit &&
  typeof hit.coverage === 'number' &&
  hit.coverage >= NEAR_MISS_COVERAGE &&
  (hit.relevanceScore ?? 0) >= NEAR_MISS_SCORE &&
  hasPositiveEvidence(hit) &&
  !!query &&
  HAS_MEASUREMENT.test(query);

/**
 * Whether the top hit is confident enough to open directly, given the runner-up.
 * A predicate over the ranked LIST, not a per-hit test: "is this the answer?"
 * cannot be decided without knowing whether anything else answers equally well.
 */
export const canAutoRedirect = (
  top: { coverage?: number; subjectCoverage?: number; specificity?: number; relevanceScore?: number } | null | undefined,
  runnerUp?: { coverage?: number; specificity?: number } | null,
  opts?: { contentTokenCount?: number },
): boolean => {
  if (!top) return false;
  if (typeof top.coverage !== 'number') {
    return (top.relevanceScore ?? 0) >= LEGACY_PERFECT_MATCH_THRESHOLD;
  }
  if (top.coverage < REDIRECT_COVERAGE) return false;
  if ((top.subjectCoverage ?? 0) < REDIRECT_SUBJECT_COVERAGE) return false;
  if ((top.specificity ?? 0) < REDIRECT_SPECIFICITY) return false;
  if (!runnerUp || typeof runnerUp.coverage !== 'number') return true;
  const coverageLead = top.coverage - runnerUp.coverage >= REDIRECT_MARGIN;
  if (coverageLead) return true;
  const specificityLead =
    (top.specificity ?? 0) - (runnerUp.specificity ?? 0) >= REDIRECT_MARGIN;
  if (!specificityLead) return false;
  // One-word class queries ("laptop", "kayak"): a more specific TITLE is a
  // model, not THE answer, when another page also covers the class fully.
  if (opts?.contentTokenCount === 1 && runnerUp.coverage >= REDIRECT_COVERAGE) {
    return false;
  }
  return true;
};

/**
 * "These words name that page": the one rule that may send someone to a page
 * they did not click. The search bar auto-opens a hit only when this passes,
 * and a one-segment URL with no page of its own is redirected only when it
 * passes (frontend/server/not-found/resolve-dead-slug.ts), so the two can
 * never disagree about what a query names.
 *
 * Auto-open only via canAutoRedirect. Title close-match and score-0 typo
 * rescues stay on the shelf — they must not skip the conversation.
 */
export function isAutoRedirectCandidate(
  hit: AlgoliaHit | undefined,
  pool: AlgoliaHit[],
  query: string,
  originalQuery?: string,
): boolean {
  if (!hit?.title) return false;
  // A page that still has to be translated is OFFERED, never opened for the
  // shopper: the translation costs real money and takes real seconds, so it
  // starts on a click. Guarding inside the predicate covers every call site.
  if (hit.untranslated) return false;
  const score = hit.relevanceScore ?? 0;
  if (
    score <= 0 &&
    (isNearExactSubjectMatch(query, hit.title) ||
      (!!originalQuery && isNearExactSubjectMatch(originalQuery, hit.title)))
  ) {
    return false;
  }
  const contentTokenCount = query.trim().split(/\s+/).filter(Boolean).length;
  return canAutoRedirect(hit, pool.find((other) => other !== hit), { contentTokenCount });
}

/**
 * Whether `hit` still answers the same query as `best`, rather than a leftover
 * from a less-precise interpretation. A coverage OR specificity lead of
 * REDIRECT_MARGIN means `best` has pulled ahead — the trailer stays off the
 * mixed product strip so a narrowing search never shows a less-precise page.
 *
 * Mirrors `canAutoRedirect`'s daylight test, including the one-word class
 * exception: "kayak" with two full-coverage pages stays mixed even when one
 * title is more specific (a model, not THE answer).
 */
export const isCompetitiveShelfHit = (
  hit: { coverage?: number; specificity?: number },
  best: { coverage?: number; specificity?: number },
  opts?: { contentTokenCount?: number },
): boolean => {
  if (typeof best.coverage !== 'number' || typeof hit.coverage !== 'number') {
    return true;
  }
  if (best.coverage - hit.coverage >= REDIRECT_MARGIN) return false;
  const specificityGap = (best.specificity ?? 0) - (hit.specificity ?? 0);
  if (specificityGap < REDIRECT_MARGIN) return true;
  return opts?.contentTokenCount === 1 && hit.coverage >= REDIRECT_COVERAGE;
};
